import * as SurveyDefinitionActions from "src/app/store/survey/actions";
import * as FromSurveyDefinition from "src/app/store/survey/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { Component, ChangeDetectionStrategy } from "@angular/core";
import { SurveyDefinitionDto, SurveyFieldDto } from "@api";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { AppRoutes } from "src/app/constants";
import { RootState } from "src/app/store";
import { first, skipWhile, switchMap, mergeMap } from "rxjs/operators";
import { PopoverButton, PopoverComponent } from "src/app/components/popover/popover.component";
import { AlertController, NavController, PopoverController } from "@ionic/angular";

@Component({
  selector: "app-survey-definition-detail",
  templateUrl: "./survey-definition-detail.page.html",
  styleUrls: ["./survey-definition-detail.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyDefinitionDetailPage {
  public readonly routes: typeof AppRoutes = AppRoutes;
  public readonly loading$: Observable<boolean> = this.store.select(FromSurveyDefinition.selectLoading);
  public breadcrumbs: any[] = [];
  public readonly surveyDefinitionSlug$: Observable<string> = this.store.select(FromRouter.selectSurveySlug);
  public readonly surveyDefinition$: Observable<SurveyDefinitionDto> = this.surveyDefinitionSlug$.pipe(
    mergeMap(surveySlug => this.store.select(FromSurveyDefinition.selectDefinitionBySlug(surveySlug))),
  );
  public surveyFields$: Observable<SurveyFieldDto[]> = this.surveyDefinitionSlug$.pipe(
    mergeMap(definitionSlug => this.store.select(FromSurveyDefinition.selectDefinitionFieldsBySlug(definitionSlug))),
  );

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly navController: NavController,
    private readonly alertController: AlertController,
    private readonly popoverController: PopoverController,
  ) {}

  public trackBy(_: number, event: SurveyDefinitionDto): string {
    return event.id;
  }

  public async presentGeneralPopover(event: Event): Promise<void> {
    const buttons: PopoverButton[] = [
      {
        text: this.i18n.instant("BUTTONS.EDIT"),
        handler: (): void => this.editSurveyDefinition(),
      },
      {
        text: this.i18n.instant("ADMIN_SURVEYS.ADD_FIELD"),
        handler: (): void => this.addSurveyField(),
      },
      { text: this.i18n.instant("BUTTONS.DELETE"), handler: (): void => this.deleteSurveyDefinition(), danger: true },
    ];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }

  public async presentSurveyFieldPopover(event: Event, surveyField: SurveyFieldDto): Promise<void> {
    event.stopPropagation();
    const buttons: PopoverButton[] = [
      {
        text: this.i18n.instant("BUTTONS.EDIT"),
        handler: (): void => this.editSurveyField(surveyField),
      },
      {
        text: this.i18n.instant("BUTTONS.VIEW"),
        handler: (): void => this.viewSurveyField(surveyField),
      },
      { text: this.i18n.instant("BUTTONS.DELETE"), handler: (): Promise<void> => this.deleteSurveyField(surveyField.id), danger: true },
    ];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }
  public viewSurveyField(surveyField: SurveyFieldDto): void {
    this.surveyDefinitionSlug$.pipe(first()).subscribe(definitionSlug => {
      this.navController.navigateForward([AppRoutes.adminSurveyDefinitions, definitionSlug, AppRoutes.surveyFields, surveyField.id]);
    });
  }
  protected ionViewWillEnter(): void {
    this.breadcrumbs = [
      { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${AppRoutes.home}` },
      { name: this.i18n.instant("ADMIN_SURVEYS.BREADCRUMB_NAME"), link: `/${AppRoutes.adminSurveyDefinitions}` },
    ];

    this.surveyDefinition$
      .pipe(
        skipWhile(surveyDefinition => !surveyDefinition),
        first(),
      )
      .subscribe(surveyDefinition => {
        this.breadcrumbs.push({ name: surveyDefinition.name, link: `/${AppRoutes.adminSurveyDefinitions}/${surveyDefinition.slug}` });
      });
  }

  protected ionViewDidEnter(): void {
    this.surveyDefinitionSlug$.pipe(first()).subscribe(definitionSlug => {
      this.store.dispatch(SurveyDefinitionActions.getDefinitionBySlug({ slug: definitionSlug }));
    });
  }

  private editSurveyDefinition(): void {
    this.surveyDefinitionSlug$.pipe(first()).subscribe(async definitionSlug => {
      this.navController.navigateForward([AppRoutes.adminSurveyDefinitions, definitionSlug, AppRoutes.edit]);
    });
  }

  private addSurveyField(): void {
    this.surveyDefinitionSlug$.pipe(first()).subscribe(async definitionSlug => {
      this.navController.navigateForward([AppRoutes.adminSurveyDefinitions, definitionSlug, AppRoutes.surveyFields, AppRoutes.add]);
    });
  }

  private editSurveyField(field: SurveyFieldDto): void {
    this.surveyDefinitionSlug$.pipe(first()).subscribe(slug => {
      this.navController.navigateForward([AppRoutes.adminSurveyDefinitions, slug, AppRoutes.surveyFields, field.id, AppRoutes.edit]);
    });
  }

  private deleteSurveyDefinition(): void {
    this.surveyDefinition$.pipe(first()).subscribe(async ({ id }) => {
      const alert = await this.alertController.create({
        header: this.i18n.instant("ADMIN_SURVEYS.DELETE_SURVEY_HEADER"),
        message: this.i18n.instant("ADMIN_SURVEYS.DELETE_SURVEY_MESSAGE"),
        buttons: [
          { role: "cancel", text: this.i18n.instant("BUTTONS.CANCEL") },
          {
            role: "destructive",
            text: this.i18n.instant("BUTTONS.CONFIRM_DELETE"),
            handler: (): void => this.store.dispatch(SurveyDefinitionActions.deleteSurveyDefinition({ definitionId: id })),
          },
        ],
      });
      await alert.present();
    });
  }

  private async deleteSurveyField(fieldId: string): Promise<void> {
    this.surveyDefinition$.pipe(first()).subscribe(async ({ id, slug }) => {
      const alert = await this.alertController.create({
        header: this.i18n.instant("ADMIN_SURVEYS.DELETE_FIELD_HEADER"),
        message: this.i18n.instant("ADMIN_SURVEYS.DELETE_FIELD_MESSAGE"),
        buttons: [
          { role: "cancel", text: this.i18n.instant("BUTTONS.CANCEL") },
          {
            role: "destructive",
            text: this.i18n.instant("BUTTONS.CONFIRM_DELETE"),
            handler: (): void => this.store.dispatch(SurveyDefinitionActions.deleteSurveyField({ definitionId: id, fieldId, slug })),
          },
        ],
      });

      await alert.present();
    });
  }
}
